import React, { useState, useEffect } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { MdSupport, MdBugReport, MdHelp, MdFeedback, MdTrendingUp } from 'react-icons/md';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const SuperAdminSupportChart = ({ API_BASE_URL, auth, userRole }) => {
  const [supportData, setSupportData] = useState({
    ticketsByType: [],
    ticketsByStatus: [],
    ticketsByPriority: [],
    monthlyTrends: [],
    userSupportStats: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview');

  useEffect(() => {
    fetchSupportData();
  }, []);

  const fetchSupportData = async () => {
    try {
      setLoading(true);
      
      // Mock support data - in real implementation, this would fetch from API
      const mockData = {
        ticketsByType: [
          { name: 'Technical Issues', count: 45, color: '#FF6384' },
          { name: 'Account Problems', count: 32, color: '#36A2EB' },
          { name: 'Feature Requests', count: 28, color: '#FFCE56' },
          { name: 'Bug Reports', count: 19, color: '#4BC0C0' },
          { name: 'General Inquiries', count: 15, color: '#9966FF' }
        ],
        ticketsByStatus: [
          { name: 'Open', count: 67, color: '#FF6384' },
          { name: 'In Progress', count: 43, color: '#FFCE56' },
          { name: 'Resolved', count: 89, color: '#4BC0C0' },
          { name: 'Closed', count: 156, color: '#36A2EB' }
        ],
        ticketsByPriority: [
          { name: 'Critical', count: 12, color: '#FF4444' },
          { name: 'High', count: 34, color: '#FF8800' },
          { name: 'Medium', count: 78, color: '#FFCC00' },
          { name: 'Low', count: 45, color: '#44AA44' }
        ],
        monthlyTrends: [
          { month: 'Jan', tickets: 45, resolved: 42 },
          { month: 'Feb', tickets: 52, resolved: 48 },
          { month: 'Mar', tickets: 38, resolved: 41 },
          { month: 'Apr', tickets: 61, resolved: 55 },
          { month: 'May', tickets: 47, resolved: 49 },
          { month: 'Jun', tickets: 53, resolved: 51 }
        ],
        userSupportStats: [
          { role: 'Field Officers', tickets: 89, avgResolutionTime: '2.3 days' },
          { role: 'MMDA Admins', tickets: 45, avgResolutionTime: '1.8 days' },
          { role: 'Super Admins', tickets: 12, avgResolutionTime: '0.9 days' },
          { role: 'System Users', tickets: 23, avgResolutionTime: '3.1 days' }
        ]
      };

      setSupportData(mockData);
    } catch (error) {
      console.error('Error fetching support data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderTicketsByTypeChart = () => {
    const data = {
      labels: supportData.ticketsByType.map(item => item.name),
      datasets: [{
        data: supportData.ticketsByType.map(item => item.count),
        backgroundColor: supportData.ticketsByType.map(item => item.color),
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 20,
            usePointStyle: true,
            font: { size: 12 }
          }
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = ((context.parsed / total) * 100).toFixed(1);
              return `${context.label}: ${context.parsed} tickets (${percentage}%)`;
            }
          }
        }
      }
    };

    return <Doughnut data={data} options={options} />;
  };

  const renderTicketsByStatusChart = () => {
    const data = {
      labels: supportData.ticketsByStatus.map(item => item.name),
      datasets: [{
        label: 'Tickets',
        data: supportData.ticketsByStatus.map(item => item.count),
        backgroundColor: supportData.ticketsByStatus.map(item => item.color),
        borderColor: supportData.ticketsByStatus.map(item => item.color),
        borderWidth: 1,
        borderRadius: 4
      }]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.label}: ${context.parsed.y} tickets`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { stepSize: 1 },
          grid: { color: 'rgba(0,0,0,0.1)' }
        },
        x: { grid: { display: false } }
      }
    };

    return <Bar data={data} options={options} />;
  };

  const renderMonthlyTrendsChart = () => {
    const data = {
      labels: supportData.monthlyTrends.map(item => item.month),
      datasets: [
        {
          label: 'New Tickets',
          data: supportData.monthlyTrends.map(item => item.tickets),
          borderColor: '#FF6384',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          tension: 0.4
        },
        {
          label: 'Resolved Tickets',
          data: supportData.monthlyTrends.map(item => item.resolved),
          borderColor: '#4BC0C0',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          tension: 0.4
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: { font: { size: 12 } }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { stepSize: 1 },
          grid: { color: 'rgba(0,0,0,0.1)' }
        },
        x: { grid: { display: false } }
      }
    };

    return <Line data={data} options={options} />;
  };

  const renderOverview = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#1976d2', textAlign: 'center' }}>
          Tickets by Type
        </h4>
        <div style={{ height: '250px' }}>
          {renderTicketsByTypeChart()}
        </div>
      </div>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#1976d2', textAlign: 'center' }}>
          Tickets by Status
        </h4>
        <div style={{ height: '250px' }}>
          {renderTicketsByStatusChart()}
        </div>
      </div>
    </div>
  );

  const renderDetailedView = () => (
    <div style={{ padding: '20px' }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#1976d2', textAlign: 'center' }}>
          Monthly Support Trends
        </h4>
        <div style={{ height: '300px' }}>
          {renderMonthlyTrendsChart()}
        </div>
      </div>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#1976d2' }}>
          Support Statistics by User Role
        </h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px'
        }}>
          {supportData.userSupportStats.map((stat, index) => (
            <div key={index} style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>
                {stat.role}
              </div>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                Total Tickets: <span style={{ fontWeight: '600', color: '#1976d2' }}>{stat.tickets}</span>
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Avg Resolution: <span style={{ fontWeight: '600', color: '#28a745' }}>{stat.avgResolutionTime}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        color: '#666'
      }}>
        Loading support analytics...
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '500px' }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e9ecef',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <MdSupport style={{ color: '#28a745', fontSize: '24px' }} />
          <h3 style={{ margin: 0, color: '#2c3e50' }}>Support Analytics</h3>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setSelectedView('overview')}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedView === 'overview' ? '#1976d2' : '#f8f9fa',
              color: selectedView === 'overview' ? 'white' : '#666',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Overview
          </button>
          <button
            onClick={() => setSelectedView('detailed')}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedView === 'detailed' ? '#1976d2' : '#f8f9fa',
              color: selectedView === 'detailed' ? 'white' : '#666',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Detailed
          </button>
        </div>
      </div>

      {/* Content */}
      {selectedView === 'overview' ? renderOverview() : renderDetailedView()}
    </div>
  );
};

export default SuperAdminSupportChart;
